/**
 * prefetch.ts
 * Prefetch all application data.
 *
 * Two-tier strategy:
 *   1. Critical — executed immediately for the current language / current page
 *      (so initial render gets the data it needs).
 *   2. Deferred — other languages / other pages, deferred until the browser is
 *      idle (or, as a fallback, after a short timeout). This dramatically
 *      reduces the number of concurrent requests on first paint without losing
 *      any prefetching behaviour.
 */

import { ALL_LANGS, ALL_PAGE_KEYS } from './base';
import { fetchProducts, fetchPortfolio, fetchNews, fetchWhereToBuy } from './content';
import { fetchHomeData } from './home';
import { fetchHeroTexts, fetchHeroMedia } from './hero';
import { fetchEsgData } from './esg';
import { fetchFooterData } from './footer';

let prefetchStarted = false;

function runWhenIdle(cb: () => void, timeoutMs = 2000) {
  if (typeof window === 'undefined') return;
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => number)
    | undefined;
  if (typeof ric === 'function') {
    ric(cb, { timeout: timeoutMs });
  } else {
    setTimeout(cb, timeoutMs);
  }
}

export function prefetchAllData(lang: string = 'ru'): void {
  if (prefetchStarted) return;
  prefetchStarted = true;

  // ─── Critical — strictly what the FIRST viewport on the home page needs ─
  // Above-the-fold content: hero (texts + media + CTAs) and the home content
  // blocks (about/stats). Everything below the fold goes to the idle phase
  // to shorten the LCP critical path (PageSpeed Insights "Network dependency
  // tree" recommendation).
  fetchHomeData(lang);
  fetchHeroTexts('home', lang);
  fetchHeroMedia();

  // ─── Deferred — needed only below the fold or on other routes ────────────
  runWhenIdle(() => {
    // Below-the-fold blocks on the home page:
    //  • FeaturedProjects (uses portfolio data) — appears AFTER hero/stats.
    //  • Footer (uses footer data) — appears at the very bottom.
    // Loading these in idle keeps the home page's critical request count
    // small without affecting visible content above the fold.
    fetchPortfolio();
    fetchFooterData();

    // Other public-list datasets — only used when user navigates to those pages.
    fetchProducts();
    fetchNews();
    fetchWhereToBuy();

    // Other languages of home data — for in-app language switching.
    for (const l of ALL_LANGS) {
      if (l === lang) continue;
      fetchHomeData(l);
    }
    // ESG data for all languages — page is rarely the first one visited.
    for (const l of ALL_LANGS) {
      fetchEsgData(l);
    }
    // Hero texts for ALL pages × OTHER languages. preloadPageCms is
    // TTL-cached and in-flight-deduplicated, so this is at most 1 DB call
    // per pageKey regardless of language count. Home for the current lang is
    // already done in the critical phase above; we still warm home for the
    // other languages here so language switching is instant.
    for (const pageKey of ALL_PAGE_KEYS) {
      for (const l of ALL_LANGS) {
        if (pageKey === 'home' && l === lang) continue; // already warmed
        fetchHeroTexts(pageKey, l);
      }
    }
  });
}
